import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { OptimizationArtifact } from '@core/models/optimization-artifacts.model';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { OptimizationArtifactsService } from '@core/services/optimization-artifacts/optimization-artifacts.service';
import { downloadBlob } from '@core/utils/download.utils';

import { ButtonComponent } from '@shared/components/button/button.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { ItemsDropdownComponent } from '@shared/components/dropdown/template/items-dropdown/items-dropdown.component';
import { DropdownItem } from '@shared/models/dropdown-item.model';

@Component({
  selector: 'app-optimization-artifacts-dropdown',
  imports: [TranslatePipe, ButtonComponent, DropdownComponent, ItemsDropdownComponent],
  templateUrl: './optimization-artifacts-dropdown.component.html'
})
export class OptimizationArtifactsDropdownComponent {
  readonly jobId = input.required<string>();

  private readonly artifactsService = inject(OptimizationArtifactsService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly ICONS = ICONS;

  protected readonly isOpen = signal(false);

  /**
   * Empty until the listing resolves, and left empty when it fails. The whole
   * control keys off this, so a job with nothing to offer — or one we cannot
   * list — shows no button at all rather than an empty menu.
   */
  private readonly artifacts = signal<OptimizationArtifact[]>([]);

  private inFlight?: Subscription;

  protected readonly hasArtifacts = computed(() => this.artifacts().length > 0);

  protected readonly artifactItems = computed<DropdownItem[]>(() =>
    this.artifacts().map((artifact) => ({
      label: artifact.fileName,
      iconClass: ICONS.download,
      action: (): void => this.downloadArtifact(artifact)
    }))
  );

  constructor() {
    effect(() => {
      this.loadArtifacts(this.jobId());
    });
  }

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  protected close = (): void => {
    this.isOpen.set(false);
  };

  private loadArtifacts(jobId: string): void {
    this.inFlight?.unsubscribe();
    this.artifacts.set([]);
    this.isOpen.set(false);
    this.inFlight = this.artifactsService
      .listArtifacts$(jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.artifacts.set(res.artifacts ?? []),
        error: () => this.artifacts.set([])
      });
  }

  private downloadArtifact(artifact: OptimizationArtifact): void {
    this.artifactsService
      .downloadArtifact$(this.jobId(), artifact.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => downloadBlob(blob, artifact.fileName),
        error: () =>
          this.notification.error(
            this.translate.instant('features.plan.jobs.artifacts.downloadButton'),
            this.translate.instant('features.plan.jobs.artifacts.downloadError')
          )
      });
  }
}
